import type { Money } from '@/shared/api/types';

import type {
  BureauProduct,
  CallBillable,
  CallKind,
  CallResult,
  ConsultationLimit,
  LimitPeriod,
  LimitScope,
} from './api';

/** Rotulos de tela do consumo das consultas ao bureau. */

export const rotuloDoEscopo: Record<LimitScope, string> = {
  empresa: 'Empresa',
  corretor: 'Corretor',
  tomador: 'Tomador',
};

export const rotuloDoPeriodoDoLimite: Record<LimitPeriod, string> = {
  mensal: 'por mes',
  diario: 'por dia',
};

export const periodosDoLimite: { value: LimitPeriod; label: string }[] = [
  { value: 'mensal', label: 'Por mes' },
  { value: 'diario', label: 'Por dia' },
];

/** O limite em palavras, com a escolha de "Sem limite" dita como tal. */
export function textoDoLimite(limite: ConsultationLimit): string {
  if (limite.unlimited || limite.limit === null) return 'Sem limite';
  return `${limite.limit} ${rotuloDoPeriodoDoLimite[limite.period]}`;
}

/** Quantidade digitada em consultas, ou nulo para o que nao e inteiro de 0 a 1.000.000. */
export function quantidadeDoTexto(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^\d{1,7}$/.test(limpo)) return null;
  const quantidade = Number(limpo);
  return quantidade <= 1000000 ? quantidade : null;
}

export const rotuloDoProduto: Record<BureauProduct, string> = {
  analise_credito_cnpj: 'Basica CNPJ',
  analise_credito_cpf: 'Basica CPF',
  analise_credito_master_cnpj: 'Master CNPJ',
  analise_credito_master_cpf: 'Master CPF',
};

export const produtos = Object.keys(rotuloDoProduto) as BureauProduct[];

export const rotuloDoResultado: Record<
  CallResult,
  { rotulo: string; tom: 'success' | 'warning' | 'danger' | 'neutral' }
> = {
  concluida: { rotulo: 'Concluida', tom: 'success' },
  parcial: { rotulo: 'Parcial', tom: 'warning' },
  falhou: { rotulo: 'Falhou', tom: 'danger' },
  incerta: { rotulo: 'Incerta', tom: 'warning' },
};

export const rotuloDoTipo: Record<CallKind, string> = {
  consulta: 'Consulta',
  autenticacao: 'Autenticacao',
};

export const rotuloDoFaturamento: Record<CallBillable, string> = {
  sim: 'Cobrada',
  nao: 'Nao cobrada',
  desconhecido: 'Cobranca incerta',
};

export const rotuloDoAmbiente: Record<string, string> = {
  teste: 'Teste',
  producao: 'Producao',
  simulado: 'SIMULADO',
};

/** Tempo de resposta legivel: milissegundos abaixo de um segundo, segundos acima. */
export function tempoDeResposta(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  const decimos = Math.round(ms / 100);
  return `${Math.floor(decimos / 10)},${decimos % 10} s`;
}

/** Dia corrente em Brasilia, no formato AAAA-MM-DD. */
export function hojeEmBrasilia(agora: Date = new Date()): string {
  const brasilia = new Date(agora.getTime() - 3 * 60 * 60 * 1000);
  return brasilia.toISOString().slice(0, 10);
}

/** Primeiro dia do mes corrente em Brasilia, no formato AAAA-MM-DD. */
export function inicioDoMesEmBrasilia(agora: Date = new Date()): string {
  return `${hojeEmBrasilia(agora).slice(0, 8)}01`;
}

/**
 * Converte o texto digitado em centavos inteiros, sem ponto flutuante.
 *
 * Aceita "1234", "1234,5", "1234,", "1.234,56" e "1234.56". A virgula sem centavos e o
 * que o campo de dinheiro mostra como "R$ 1.234,00" ao sair dele. Mais de duas casas
 * decimais e recusado, e nao arredondado, como no servidor (ADR 0004). Devolve nulo para
 * texto que nao e valor.
 */
export function centavosDoTexto(texto: string): number | null {
  let limpo = texto.trim().replace(/^R\$\s*/, '');
  if (limpo === '') return null;
  if (limpo.includes(',')) {
    limpo = limpo.replace(/\./g, '').replace(',', '.');
  }
  const partes = /^(\d{1,13})(?:\.(\d{0,2}))?$/.exec(limpo);
  if (!partes) return null;
  const [, inteiros = '0', decimais = ''] = partes;
  const centavos = Number(inteiros) * 100 + Number(decimais.padEnd(2, '0'));
  return Number.isSafeInteger(centavos) ? centavos : null;
}

export function reaisDeCentavos(centavos: number): Money {
  return { amount: centavos, currency: 'BRL', exponent: 2 };
}
